// @ts-nocheck
import { all, call, put, select, takeLatest, getContext } from 'redux-saga/effects'
import { flatten, formatPointToLocation, getPosition, getRegion, getServiceAreaCenter, isPointInsideOutlines, makePointOnMap, sanitizeTelephone } from '../../helpers'
import moment from 'moment'
import { IPaymentActions, IPaymentStatus, ISearchedType, IServiceInfo, IStop, IStopAvailabilityTimes } from '../../models'
import { urls } from '../../routing'
import {
  GoogleMapsService,
  LiteralsService,
  ReservationService,
  ServicesInfoService,
  StorageService,
  TownService,
  UserService
} from '../../services'
import {
  confirmNewReservationFailedAction,
  confirmNewReservationSuccessfulAction,
  fetchDestinationBusStopsAction,
  fetchDestinationBusStopsSuccesfulAction,
  fetchReservationsAction,
  fetchReturnRouteOptionsSuccessfulAction,
  fetchRouteOptionsSuccessfulAction,
  fetchRouteOptionsSuccessfulEcorridorAction,
  fetchServicesSuccessfulAction,
  resetNewReservationFieldsAction,
  searchStopsAction,
  setBusStopAction,
  setIsConfirmingNewReservationAction,
  setIsFetchingDestinationBusStopsAction,
  setIsFetchingReturnRouteOptionsAction,
  setIsFetchingRouteOptionsAction,
  setIsFetchingRouteOptionsEcorridorAction,
  setIsFetchingServicesAction,
  addCachedAddressAction,
  setCachedAddressesAction,
  setIsFetchingStopAvailabilityTimesAction,
  setStopAvailabilityTimesAction,
  setIsMapCenterWithinOutlinesAction,
  setIsSeachingAction,
  setNotificationAction,
  setPaymentFailed,
  setSearchedStopResultAction,
  setSearchStopsAction,
  setStripeAccountId,
  setUserLocationAction
} from '../actions'
import { IStoreState } from '../states'
import { BusStopsStepTypeKeys, ConfirmStepTypeKeys, ISetNotificationAction, RouteOptionStepTypeKeys } from '../types'
import { selectUserTown } from './helpers'
import { Stripe } from '@stripe/stripe-js'
import { Application } from '../../config'
import { HttpStatusCode } from 'axios'

let token = ''
let env = 'dev'

const { isIframe } = Application

// Worker Saga
function* fetchServicesAsync() : any {
  const routerHistory = yield getContext('routerHistory')
  try {
    yield put(setIsFetchingServicesAction())
    const isEdit = yield select(
      (state: IStoreState) => state.newReservation.isEdit
    )

    // Guest mode, like the iframe, has no user profile: its town lives in the URL.
    const isGuest = yield select(
      (state: IStoreState) => state.userInfo.isGuest
    )

    let serviceInfos
    if (!isEdit) {
      if (isIframe || isGuest) {
        let townId = new URLSearchParams(location.search).get('townId')
        if (!townId) townId = routerHistory.location.state?.townId
        serviceInfos = yield call(TownService.getServices, Number(townId))
      } else {
        const town = yield selectUserTown()
        serviceInfos = yield call(TownService.getServices, town.id)
      }
    } else {
      const pickedService = yield select(
        (state: IStoreState) => state.newReservation.pickedService
      )
      const pickedTownId = yield select(
        (state: IStoreState) => state.newReservation.pickedTown
      )
      const service = yield call(
        ServicesInfoService.getServicesByServiceId,
        pickedService
      )
      serviceInfos = service.serviceInfo

      const pickedOriginBusStop = yield select(
        (state: IStoreState) => state.newReservation.pickedOriginBusStop
      )
      yield put(
        fetchDestinationBusStopsAction({
          idTown: pickedTownId,
          stop: pickedOriginBusStop
        })
      )
    }
    yield put(fetchServicesSuccessfulAction(serviceInfos))
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
}

const DEGREE_IN_METERS = 111320

function* fetchStopSearchAsync(action: any) : any {
  let stopServices = []
  const serviceInfos = yield select((state: IStoreState) => state.newReservation.serviceInfos)
  const destinationStops: IStop[] = yield select ((state: IStoreState) => state.newReservation.destinationBusStops)
  if (action.payload && serviceInfos && serviceInfos.length > 0) {
    stopServices = serviceInfos
      .filter((s: IServiceInfo) => s.selected)
      .map((s: IServiceInfo) =>
        s.stops
          .filter((st: IStop) => {
            const stopNameExists = st.name.toUpperCase().includes(action.payload.toUpperCase())
            const stopCodeExists = st.code.toUpperCase().includes(action.payload.toUpperCase())
            const isDestinationEnabled = destinationStops.some(stop => stop.id === st.id)

            if (destinationStops.length) return isDestinationEnabled && (stopNameExists || stopCodeExists)
            return stopNameExists || stopCodeExists
          })
          .map(st => ({ ...st, serviceInfo: s }))
      )
      .reduce((a: IStop[], b: IStop[]) => a.concat(b))
  }

  // Door to door can start anywhere, so the search also offers addresses; for
  // fixed-stop services an address is not bookable and would only mislead.
  const isSearchingCoordsInMap = yield select((state: IStoreState) => state.newReservation.isSearchingCoordsInMap)
  if (isSearchingCoordsInMap && action.payload) {
    const places = yield call(GoogleMapsService.getPlaces, action.payload, searchAreaBias(serviceInfos))

    if (places.length) {
      stopServices = [{ id: places[0].id, name: places[0].name, code: places[0].id, fromGoogle: true }, ...stopServices]
    }
  }

  yield put(setSearchStopsAction(stopServices))
}

// Bias the address results to the service area, as the app does.
function searchAreaBias(serviceInfos: IServiceInfo[]) {
  const center = getServiceAreaCenter(serviceInfos)
  if (!center) return undefined

  const region = getRegion({
    features: serviceInfos.map(serviceInfo => serviceInfo.stops.map(stop => stop.location)).reduce(flatten, [])
  })
  const radius = region ? Math.max(region.latitudeDelta, region.longitudeDelta) / 2 * DEGREE_IN_METERS : undefined

  return { center, radius }
}

// Caches the last result so reopening the same stop (same day and services) reuses it instead of
// calling the BE again. Keyed by day, not by minute, so the "now" fetch and a later "start of day"
// fetch of the same day match; a short TTL keeps the live times fresh.
const STOP_AVAILABILITY_TIMES_CACHE_TTL = 120000 // 2 min
let lastStopAvailabilityTimesFetch: { key: string, at: number, data: IStopAvailabilityTimes[] } | null = null

function* fetchStopAvailabilityTimesAsync(action: any): any {
  const { stopId, date, serviceInfos } = action.payload
  const routerHistory = yield getContext('routerHistory')
  const { isEdit, pickedTown } = yield select((state: IStoreState) => state.newReservation)
  const town = yield selectUserTown()
  const stateTownId = routerHistory.location.state?.townId
  const townId = isEdit ? pickedTown : stateTownId ? stateTownId : town.id

  const serviceInfoIds = (serviceInfos || []).map((serviceInfo: IServiceInfo) => serviceInfo.id)
  const key = `${townId}|${stopId}|${moment(date).format('YYYYMMDD')}|${serviceInfoIds.join(',')}`

  if (
    lastStopAvailabilityTimesFetch &&
    lastStopAvailabilityTimesFetch.key === key &&
    Date.now() - lastStopAvailabilityTimesFetch.at < STOP_AVAILABILITY_TIMES_CACHE_TTL
  ) {
    yield put(setStopAvailabilityTimesAction(lastStopAvailabilityTimesFetch.data))
    return
  }

  try {
    yield put(setIsFetchingStopAvailabilityTimesAction(true))
    const times = yield call(TownService.getAvailabilitiesFromStop, townId, stopId, new Date(date), serviceInfoIds)
    lastStopAvailabilityTimesFetch = { key, at: Date.now(), data: times }
    yield put(setStopAvailabilityTimesAction(times))
  } catch (e) {
    // A stop with no service that day answers with an error too: show it as empty rather
    // than blocking the map with a notification.
    yield put(setStopAvailabilityTimesAction([]))
  }
}

function* loadCachedAddressesAsync(): any {
  yield put(setCachedAddressesAction(StorageService.getCachedAddresses()))
}

function* persistCachedAddressesAsync(): any {
  const cachedAddresses = yield select((state: IStoreState) => state.newReservation.cachedAddresses)
  StorageService.setCachedAddresses(cachedAddresses)
}

function* fetchStopSearchLocationAsync(action: any) : any {
  const pickedOriginBusStop = yield select(
    (state: IStoreState) => state.newReservation.pickedOriginBusStop
  )
  let coordinates
  let response
  let mapCoordinates

  switch (action.payload.type) {
    case ISearchedType.location:
      response = yield call(getPosition)
      coordinates = formatPointToLocation(response.coords)
      yield put(setUserLocationAction({
        latitude: coordinates.geometry.coordinates[0],
        longitude: coordinates.geometry.coordinates[1]
      }))
      break
    case ISearchedType.google: {
      const place = yield call(GoogleMapsService.getCoordinatesPlace, action.payload.id)
      if (place.latitude == null || place.longitude == null) break

      const serviceInfos = yield select((state: IStoreState) => state.newReservation.serviceInfos)
      // An address outside the service area cannot be served, so it is not picked.
      if (!isPointInsideOutlines(serviceInfos, place)) {
        yield put(setIsMapCenterWithinOutlinesAction(false))
        break
      }

      // Stops carry GeoJSON, which is [longitude, latitude] — the opposite of
      // what formatPointToLocation returns.
      const location = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: [place.longitude, place.latitude] }
      }

      yield put(setIsMapCenterWithinOutlinesAction(true))
      yield put(setBusStopAction({
        stop: { ...action.payload, location, isFixedStop: false } as unknown as IStop
      }))
      yield put(addCachedAddressAction({
        id: String(action.payload.id),
        name: action.payload.name,
        location,
        usedAt: Date.now()
      }))
      break
    }
    case ISearchedType.stop:
      const town = yield selectUserTown()
      mapCoordinates = makePointOnMap(
        action.payload.location.geometry.coordinates,
        0
      )
      coordinates = formatPointToLocation(mapCoordinates)
      yield put(setBusStopAction({ stop: action.payload }))
      if (!pickedOriginBusStop) {
        yield put(fetchDestinationBusStopsAction({ idTown: town.id, stop: action.payload }))
      }
      break
    default:
      coordinates = null
      break
  }

  yield put(setIsSeachingAction(false))
  yield put(setSearchedStopResultAction({ name: '' }))
  yield put(searchStopsAction(''))
}

function* fetchDestinationBusStopsAsync(action: any) : any {
  let stops = []
  try {
    yield put(setIsFetchingDestinationBusStopsAction())
    const isSearchingCoordsInMap = yield select((state: IStoreState) => state.newReservation.isSearchingCoordsInMap)
    const { idTown, stop } = action.payload
    const [longitude, latitude] = stop.location?.geometry?.coordinates ?? []

    // Door to door origins are arbitrary points, so destinations are resolved
    // from coordinates; a snapped fixed stop still narrows it down by id.
    if (isSearchingCoordsInMap && latitude != null && longitude != null) {
      stops = yield call(
        TownService.getDestinationBusStopsByPosition,
        idTown,
        latitude,
        longitude,
        typeof stop.id === 'number' ? stop.id : undefined
      )
    } else {
      stops = yield call(TownService.getDestinationBusStops, idTown, stop.id)
    }
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchDestinationBusStopsSuccesfulAction(stops))
}

function* fetchRouteOptionsAsync(action: any) : any {

  let options = []
  let errorCode = 200
  const routerHistory = yield getContext('routerHistory')

  yield put(setIsFetchingRouteOptionsAction())
  const {
    pickedDate,
    pickedSeats,
    pickedPrmSeats,
    specificDate,
    isEdit,
    pickedTown
  } = yield select((state: IStoreState) => state.newReservation)

  const town = yield selectUserTown()
  const townId = routerHistory.location.state?.townId

  const originStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedOriginBusStop &&
      state.newReservation.pickedOriginBusStop.id
  )
  const destinationStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedDestinationBusStop &&
      state.newReservation.pickedDestinationBusStop.id
  )

  let alternatives = action.payload.alternatives ?? 2
  let onlyToday = action.payload.onlyToday ?? false

  const { isSearchingCoordsInMap, pickedOriginBusStop, pickedDestinationBusStop } =
    yield select((state: IStoreState) => state.newReservation)

  if (!isSearchingCoordsInMap && !originStopId && !destinationStopId) {
    yield put(fetchRouteOptionsSuccessfulAction({ availabilities: [] } as any, 200))
    return
  }

  try {
    const currentTownId = isEdit ? pickedTown : townId ? townId : town.id

    // Door to door stops have no id the backend knows, so availabilities are
    // looked up from the coordinates the user dropped the pin on.
    if (isSearchingCoordsInMap && pickedOriginBusStop && pickedDestinationBusStop) {
      const [inLongitude, inLatitude] = pickedOriginBusStop.location.geometry.coordinates
      const [outLongitude, outLatitude] = pickedDestinationBusStop.location.geometry.coordinates

      options = yield call(
        TownService.getAvailabilitiesByPositions,
        currentTownId,
        inLatitude,
        inLongitude,
        pickedOriginBusStop.name,
        outLatitude,
        outLongitude,
        pickedDestinationBusStop.name,
        pickedSeats,
        pickedPrmSeats,
        pickedDate,
        specificDate,
        onlyToday,
        action.payload.reservationId
      )
    } else {
      options = yield call(
        TownService.getAvailabilitiesAndFailures,
        currentTownId,
        originStopId ?? 0,
        destinationStopId ?? 0,
        pickedSeats,
        pickedPrmSeats,
        pickedDate,
        specificDate,
        alternatives,
        onlyToday,
        action.payload.reservationId
      )
    }
  } catch (error: any) {
    errorCode = error.status
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchRouteOptionsSuccessfulAction(options, errorCode))
  if (action.payload.navigate) yield call(routerHistory.push, urls.newReservationRouteOptionsStep, routerHistory.location.state)
}

function* fetchReturnRouteOptionsAsync(action: any) : any {
  let options = []
  let errorCode = 200
  const routerHistory = yield getContext('routerHistory')

  yield put(setIsFetchingReturnRouteOptionsAction())
  const {
    returnPickedDate,
    pickedSeats,
    pickedPrmSeats,
    returnSpecificDate,
    isEdit,
    pickedTown
  } = yield select((state: IStoreState) => state.newReservation)

  const town = yield selectUserTown()
  const townId = routerHistory.location.state?.townId

  const originStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedDestinationBusStop &&
      state.newReservation.pickedDestinationBusStop.id
  )
  const destinationStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedOriginBusStop &&
      state.newReservation.pickedOriginBusStop.id
  )

  let alternatives = action.payload.alternatives ?? 2
  let onlyToday = action.payload.onlyToday ?? false

  if (!originStopId && !destinationStopId) {
    yield put(fetchReturnRouteOptionsSuccessfulAction({ availabilities: [] } as any, 200))
    return
  }

  try {
    options = yield call(
      TownService.getAvailabilitiesAndFailures,
      isEdit ? pickedTown : townId ? townId : town.id,
      originStopId ?? 0,
      destinationStopId ?? 0,
      pickedSeats,
      pickedPrmSeats,
      returnPickedDate,
      returnSpecificDate,
      alternatives,
      onlyToday,
      action.payload.reservationId
    )
  } catch (error: any) {
    errorCode = error.status
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchReturnRouteOptionsSuccessfulAction(options, errorCode))
  if (action.payload.navigate) yield call(routerHistory.push, urls.newReservationRouteOptionsStep, routerHistory.location.state)
}


function* fetchRouteOptionsAsyncEcorridor() : any {
  let options = []
  const routerHistory = yield getContext('routerHistory')
  const queryParams = new URLSearchParams(window.location.search)
  const pickedService = Number(queryParams.get('service'))
  const originStopId = Number(queryParams.get('origin'))
  const destinationStopId = Number(queryParams.get('dest'))
  const pickedSeats = Number(queryParams.get('passengers'))
  const pickedPrmSeats = Number(queryParams.get('prm'))
  const pickedDate = new Date(Number(queryParams.get('datetime')))
  const specificDate = true
  token = queryParams.get('token') || ''
  env = queryParams.get('env') || 'dev'

  if (pickedService !== 0) {
    yield put(setIsFetchingRouteOptionsEcorridorAction())
    try {
      options = yield call(
        ServicesInfoService.getAvailabilities,
        pickedService,
        originStopId ?? 0,
        destinationStopId ?? 0,
        pickedSeats,
        pickedPrmSeats,
        pickedDate,
        specificDate,
        { token, env }
      )
    } catch (error) {
      yield put(
        setNotificationAction({
          isError: true,
          label: LiteralsService.get('serverError', true)
        })
      )
    }
    yield put(fetchRouteOptionsSuccessfulEcorridorAction(options))
  }
  yield call(routerHistory.push, urls.newReservationRouteOptionsStep)
}

function* fetchEarlierRouteOptionsAsync() : any {
  let options = []
  let errorCode = 200
  yield put(setIsFetchingRouteOptionsAction())

  const {
    pickedService,
    pickedDate,
    pickedSeats,
    pickedPrmSeats,
    specificDate
  } = yield select((state: IStoreState) => state.newReservation)

  const originStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedOriginBusStop &&
      state.newReservation.pickedOriginBusStop.id
  )
  const destinationStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedDestinationBusStop &&
      state.newReservation.pickedDestinationBusStop.id
  )
  const availabilityId = yield select(
    (state: IStoreState) => state.newReservation.routeOptions[0].id
  )

  try {
    options = yield call(
      ServicesInfoService.getAvailabilitiesEarlier,
      pickedService,
      originStopId ?? 0,
      destinationStopId ?? 0,
      pickedSeats,
      pickedPrmSeats,
      pickedDate,
      availabilityId,
      specificDate
    )
  } catch (error: any) {
    errorCode = error.status
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchRouteOptionsSuccessfulAction(options, errorCode))
}

function* fetchLaterRouteOptionsAsync() : any {
  let options = []
  let errorCode = 200
  yield put(setIsFetchingRouteOptionsAction())

  const {
    pickedService,
    pickedDate,
    pickedSeats,
    pickedPrmSeats,
    specificDate
  } = yield select((state: IStoreState) => state.newReservation)

  const originStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedOriginBusStop &&
      state.newReservation.pickedOriginBusStop.id
  )
  const destinationStopId = yield select(
    (state: IStoreState) =>
      state.newReservation.pickedDestinationBusStop &&
      state.newReservation.pickedDestinationBusStop.id
  )
  const availabilityId = yield select(
    (state: IStoreState) => state.newReservation.routeOptions[0].id
  )

  try {
    options = yield call(
      ServicesInfoService.getAvailabilitiesLater,
      pickedService,
      originStopId ?? 0,
      destinationStopId ?? 0,
      pickedSeats,
      pickedPrmSeats,
      pickedDate,
      availabilityId,
      specificDate
    )
  } catch (error: any) {
    errorCode = error.status
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }

  yield put(fetchRouteOptionsSuccessfulAction(options, errorCode))
}

function* confirmNewReservationAsync() : any {
  const routerHistory = yield getContext('routerHistory')
  yield put(setIsConfirmingNewReservationAction())

  const {
    isEdit,
    pickedAvailability,
    pickedReservationId,
    pickedRelatedReservationId,
    pickedSeats,
    pickedPrmSeats,
    paymentAction,
    returnPickedAvailability,
    activePassId
  } = yield select((state: IStoreState) => state.newReservation)

  const {
    paymentMethodId,
  } = yield select((state: IStoreState) => state.userInfo)

  const {
    name,
    lastName,
    email,
    callingCode,
    phone
  } = yield select((state: IStoreState) => state.signUp)

  const {
    clientInfo,
  } = yield select((state: IStoreState) => state.configuration)

  const userData = {
    firstName: name,
    lastName: lastName,
    email: email,
    phone: sanitizeTelephone(phone, callingCode),
    town: {id: routerHistory.location.state?.townId}
  }

  let reservationIdsToSend: any[] = []
  let userCreatedFlag = false
  let paymentClientSecretToSend = []

  const { accountId } = yield call(checkStripeAccountId, pickedAvailability.serviceId)
  yield put(setStripeAccountId(accountId))

  try {
    if (!isEdit) {
      if (returnPickedAvailability) {
        const response = yield call(
          ReservationService.postMultipleReservation,
          [pickedAvailability, returnPickedAvailability],
          pickedSeats,
          pickedPrmSeats,
          paymentMethodId,
          isIframe ? userData : undefined
        )
        reservationIdsToSend.push(response[0].reservationId)
        paymentClientSecretToSend.push(response[0].paymentClientSecret)
        userCreatedFlag = userCreatedFlag || response[0].userCreated
      } else {
        const isSearchingCoordsInMap = yield select((state: IStoreState) => state.newReservation.isSearchingCoordsInMap)
        const { reservationId, userCreated, paymentClientSecret } = yield call(
          ReservationService.postReservation,
          pickedAvailability!,
          pickedSeats,
          pickedPrmSeats,
          pickedAvailability.serviceId,
          paymentMethodId,
          isIframe ? userData : undefined,
          isSearchingCoordsInMap,
          activePassId
        )
        reservationIdsToSend.push(reservationId)
        paymentClientSecretToSend.push(paymentClientSecret)
        userCreatedFlag = userCreated
      }
    } else {
      if (pickedRelatedReservationId) {
        yield call(
          ReservationService.updateMultipleReservations,
          [pickedReservationId, pickedRelatedReservationId],
          [pickedAvailability!, returnPickedAvailability],
          pickedSeats,
          pickedPrmSeats,
          [pickedAvailability.serviceId, returnPickedAvailability.serviceId]
        )
        reservationIdsToSend.push(pickedReservationId)
      } else {
        yield call(
          ReservationService.updateReservation,
          pickedReservationId!,
          pickedAvailability!,
          pickedSeats,
          pickedPrmSeats,
          pickedAvailability.serviceId
        )
        reservationIdsToSend.push(pickedReservationId)
      }
    }

    let reservationPaymentStatus = ''
    if (paymentAction === IPaymentActions.PAY) {
      const { stripe } = yield select((state: IStoreState) => state.configuration)
      const { notification, paymentStatus, success } = yield call(confirmStripePayment, reservationIdsToSend, isEdit, stripe, paymentClientSecretToSend, routerHistory)
      if (notification) yield put(setNotificationAction(notification))
      if (isIframe) {
        reservationPaymentStatus = paymentStatus[0]
      } else {
        yield* reservationIdsToSend.map(function* (reservationId) : any {
          reservationPaymentStatus = success ? IPaymentStatus.CREATED : IPaymentStatus.FAILED
          while (reservationPaymentStatus === IPaymentStatus.CREATED) {
            const { paymentStatus } = yield call(ReservationService.getReservation, reservationId)
            reservationPaymentStatus = paymentStatus
          }
        })
      }
    } else if (paymentAction !== IPaymentActions.PAY) {
      yield put(
        setNotificationAction({
          isError: false,
          label: LiteralsService.get(isEdit ? 'bookUpdated' : 'bookConfirmed', true)
        })
      )
    }

    if (
      paymentAction !== IPaymentActions.PAY ||
      paymentAction === IPaymentActions.PAY && reservationPaymentStatus.toLowerCase() === 'succeeded'
    ) {
      if (isIframe) {
        let price = pickedAvailability.price
        if (returnPickedAvailability) price = price + returnPickedAvailability.price

        let eventItems: any = [{
          item_id: pickedAvailability.serviceInfo.id,
          item_name: pickedAvailability.serviceInfo.name,
          quantity: pickedAvailability.outStops.length,
          price: pickedAvailability.price
        }];
        if (returnPickedAvailability) {
          eventItems.push({
            item_id: pickedAvailability.serviceInfo.id,
            item_name: pickedAvailability.serviceInfo.name,
            quantity: returnPickedAvailability.outStops.length,
            price: returnPickedAvailability.price
          })
        }

        let postMessageBody = {
          event: 'nemi_purchase_complete',
          transaction_id: paymentClientSecretToSend[0],
          value: price,
          currency: "EUR",
          items: eventItems
        }
        window.parent.postMessage(postMessageBody, clientInfo.url)

        yield call(routerHistory.push, urls.newReservationCompletedStep)
        yield put(resetNewReservationFieldsAction())
      } else {
        const size = yield select((state: IStoreState) => state.reservations.size)
        yield call(routerHistory.push, urls.reservations)
        yield put(resetNewReservationFieldsAction())
        yield put(fetchReservationsAction(size, 1))
      }
      yield put(setStripeAccountId(''))
      yield put(confirmNewReservationSuccessfulAction(userCreatedFlag))
    }

  } catch (error: any) {
    yield put(setStripeAccountId(''))
    yield put(confirmNewReservationFailedAction())
    yield put(setPaymentFailed())
    let errorMessage = 'bookFailed'
    const errorBody = error?.data
    switch(error.status) {
      case HttpStatusCode.SeeOther:
      case HttpStatusCode.Conflict:
        errorMessage = 'duplicatedReservation'
        break
      case HttpStatusCode.PaymentRequired:
        errorMessage = 'payFailed'
        break
      case HttpStatusCode.NotAcceptable:
        errorMessage = 'maxCapacityExceeded'
        break
      case HttpStatusCode.PreconditionFailed:
        errorMessage = 'preconditionFailed'
        break
      case HttpStatusCode.UnprocessableEntity:
        errorMessage = 'infiesibleReservation'
        break
    }
    const label = errorBody?.message ?
      LiteralsService.get(errorBody.message, true) :
      LiteralsService.get(errorMessage, true)
    yield put(
      setNotificationAction({
        isError: true,
        label
      })
    )
  }
}

async function checkStripeAccountId(serviceId: number) {
  const { accountId } = await UserService.getAccountId(serviceId)
  return { accountId }
}

async function confirmStripePayment(resId: number[], isEdit: boolean, stripe: Stripe, paymentClientSecretToSend?: string[], history?: any) {
  let notification: ISetNotificationAction
  let paymentStatus: any[] = []
  let success: boolean = false;
  try {
    await Promise.all(
      resId.map(async (reservationId, index) => {
        let clientSecret = ''
        if (paymentClientSecretToSend && paymentClientSecretToSend?.length > 0) {
          clientSecret = paymentClientSecretToSend[index]
        } else {
          const { paymentClientSecret } =  await ReservationService.getReservation(reservationId)
          clientSecret = paymentClientSecret
        }
        stripe.elements({
          clientSecret
        })
        const { paymentIntent } = await stripe.confirmPayment({
          clientSecret,
          redirect: 'if_required'
        })
        paymentStatus.push(paymentIntent?.status)
        switch (paymentIntent?.status) {
          case 'succeeded':
            success = true
            notification = setNotificationAction({
              isError: false,
              label: LiteralsService.get(isEdit ? 'bookUpdated' : 'bookConfirmed', true)
            })
            break;
          case 'canceled':
          case 'requires_payment_method':
          default:
            confirmNewReservationFailedAction()
            setPaymentFailed()
            history && history.push(`${urls.reservationDetails}/${reservationId}`)
            notification = setNotificationAction({
              isError: true,
              label: LiteralsService.get('payFailed', true)
            })
            break;
        }
      })
    )
    return {
      notification: notification!,
      paymentStatus,
      success
    }
  } catch (error) {
    confirmNewReservationFailedAction()
    setPaymentFailed()
    notification = setNotificationAction({
      isError: true,
      label: LiteralsService.get('payFailed', true)
    })
  }
}

// Watcher Saga:
function* watchFetchServicesAsync() {
  yield takeLatest(BusStopsStepTypeKeys.FETCH_SERVICES, fetchServicesAsync)
}

function* watchFetchStopSearchAsync() {
  yield takeLatest(BusStopsStepTypeKeys.SEARCH_STOPS, fetchStopSearchAsync)
}

function* watchFetchStopAvailabilityTimes() {
  yield takeLatest(BusStopsStepTypeKeys.FETCH_STOP_AVAILABILITY_TIMES, fetchStopAvailabilityTimesAsync)
}

function* watchLoadCachedAddresses() {
  yield takeLatest(BusStopsStepTypeKeys.LOAD_CACHED_ADDRESSES, loadCachedAddressesAsync)
}

function* watchAddCachedAddress() {
  yield takeLatest(BusStopsStepTypeKeys.ADD_CACHED_ADDRESS, persistCachedAddressesAsync)
}

function* watchFetchStopSearchLocationAsync() {
  yield takeLatest(BusStopsStepTypeKeys.SEARCH_STOP_COORDINATES, fetchStopSearchLocationAsync)
}

function* watchFetchDestinationBusStopsAsync() {
  yield takeLatest(BusStopsStepTypeKeys.FETCH_DESTINATION_BUS_STOPS, fetchDestinationBusStopsAsync)
}

function* watchFetchRouteOptionsAsync() {
  yield takeLatest(RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS, fetchRouteOptionsAsync)
}

function* watchFetchRouteOptionsAsyncByParams() {
  yield takeLatest(RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS_ECORRIDOR, fetchRouteOptionsAsyncEcorridor)
}

function* watchFetchEarlierRouteOptionsAsync() {
  yield takeLatest(RouteOptionStepTypeKeys.FETCH_EARLIER_ROUTE_OPTIONS, fetchEarlierRouteOptionsAsync)
}

function* watchFetchLaterRouteOptionsAsync() {
  yield takeLatest(RouteOptionStepTypeKeys.FETCH_LATER_ROUTE_OPTIONS, fetchLaterRouteOptionsAsync)
}

function* watchConfirmNewReservationAsync() {
  yield takeLatest(ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION, confirmNewReservationAsync)
}

function* watchFetchReturnRouteOptionsAsync() {
  yield takeLatest(RouteOptionStepTypeKeys.FETCH_RETURN_ROUTE_OPTIONS, fetchReturnRouteOptionsAsync)
}

// single entry point to start all Sagas at once
export default function* rootSaga() {
  yield all([
    watchFetchServicesAsync(),
    watchFetchStopSearchAsync(),
    watchFetchStopSearchLocationAsync(),
    watchFetchStopAvailabilityTimes(),
    watchLoadCachedAddresses(),
    watchAddCachedAddress(),
    watchFetchDestinationBusStopsAsync(),
    watchFetchRouteOptionsAsync(),
    watchFetchRouteOptionsAsyncByParams(),
    watchFetchEarlierRouteOptionsAsync(),
    watchFetchLaterRouteOptionsAsync(),
    watchConfirmNewReservationAsync(),
    watchFetchReturnRouteOptionsAsync()
  ])
}
